<template>
  <div class="agreement-container">
    <h1 class="page-heading">{{ appName }} User Agreement</h1>
    <div class="content-block">
      Please review this User Agreement thoroughly prior to registering an account. This document defines the rights and obligations of all users and protects legitimate interests for every party involved.
      Please pay close attention to highlighted terms across the whole text and make sure you fully understand all listed provisions.
      You may not register, sign in or access our software and services unless you accept all terms stated below. By completing registration, logging in or using our services, you confirm that you have read, understood and agreed to be legally bound by this Agreement.
      We reserve the right to revise, supplement or remove any part of this Agreement, including supplementary rules, privacy policies and feature terms at any time without prior notice. Updated versions will be published within our software and services. If you disagree with the revised content, you must stop using our services immediately. Your continued use after updates means you accept the new agreement.
    </div>

    <h2 class="section-heading">1. Account Rules</h2>
    <div class="content-block">
      1.1 To use our services, you need to create or obtain a user account. We retain the right to adjust the account application process as needed.
      <br /><br />
      1.2 Once you complete registration, you authorize us to activate accounts for other services operated by our company simultaneously. The actual available products depend on our daily operations.
      <br /><br />
      1.3 All accounts belong to our company, and you are only granted personal usage rights. It is strictly forbidden to gift, lend, rent, transfer or sell your account to any third party.
      <br /><br />
      1.4 You are fully responsible for keeping your account and password secure. If your account gets compromised due to your negligence, you shall take all corresponding responsibilities. All operations performed under your account will be deemed as your own behaviors. Please contact us right away once you find any suspicious unauthorized access. You are not allowed to use other users' accounts or passwords under any circumstances.
      <br /><br />
      1.5 You must follow this Agreement and use the software in a proper manner. We have the right to suspend or terminate your service access if you violate any clauses.
      <br /><br />
      1.6 If your account remains inactive for one full year, we may delete or reclaim it without advance notice to save server resources, and you will bear all associated risks. We also reserve the right to take back any account and username at any time at our own discretion.
      <br /><br />
    </div>

    <h2 class="section-heading">2. Service Scope</h2>
    <div class="content-block">
      2.1 The functional modules of our software are provided based on actual operation, including but not limited to video chat, in-app purchases and virtual gift delivery. We may adjust service contents and functions at any time without sending prior notifications.
      <br /><br />
      2.2 You agree that we may send commercial promotions and business notices via email, app push, SMS, web pages and other legal channels. Due to the characteristics of mobile network environment, we may collect basic information from your mobile device.
      <br /><br />
    </div>

    <h2 class="section-heading">3. User Data Protection</h2>
    <div class="content-block">
      3.1 During account registration and service usage, you may be required to submit identity information as required by relevant laws and regulations. Your service access will be denied or restricted if the submitted information is incomplete, inaccurate or fails to meet regulatory requirements.
      <br /><br />
      3.2 To optimize service experience and improve product technology, we may collect, use or share your information with authorized third parties. Please refer to our Privacy Policy for detailed explanations.
    </div>

    <h2 class="section-heading">4. Content Specifications & Usage Rules</h2>
    <div class="content-block">
      4.1 The term "Content" refers to all materials you create, upload, copy, publish or share on the platform. It covers avatars, usernames, personal profiles, texts, audios, images, videos, messages, links and all other data generated during service use.
      <br /><br />
      4.2 You must not use this software to create, upload, distribute or engage in any of the following content or improper behaviors:
      <br /><br />
      1) Content that endangers national security, leaks confidential information, subverts state power or undermines national unity;
      <br /><br />
      2) Content that harms national reputation and legitimate interests;
      <br /><br />
      3) Content that incites ethnic hatred, discrimination or damages ethnic solidarity;
      <br /><br />
      4) Content that violates national religious policies or promotes cults and feudal superstition;
      <br /><br />
      5) Content that spreads rumors, disturbs public order or social stability;
      <br /><br />
      6) Content containing obscene, pornographic or sexually suggestive elements;
      <br /><br />
      7) Content that advocates gambling, violence, murder or terrorist activities;
      <br /><br />
      8) Content that insults, defames others, infringes legal rights or involves fraud;
      <br /><br />
      9) Content using abusive, intimidating or threatening words;
      <br /><br />
      10) Sending harassing messages, spam, malicious programs or phishing links;
      <br /><br />
      11) Disclosing or invading other people's privacy and personal information;
      <br /><br />
      12) Acts that infringe others' right of reputation, portrait right, intellectual property, trade secrets and other legal rights;
      <br /><br />
      13) Any content that causes harm to minors in any form;
      <br /><br />
      14) Stalking or harassing other platform users;
      <br /><br />
      15) Publishing false information or impersonating others by stealing personal data;
      <br /><br />
      16) Coercing or inducing other users to follow accounts, click links or share content;
      <br /><br />
      17) Fabricating facts or hiding the truth to mislead others;
      <br /><br />
      18) Posting commercial advertisements and sales promotion content without permission;
      <br /><br />
      19) Creating a large number of fake accounts by technical means;
      <br /><br />
      20) Removing copyright marks from the software or its copies;
      <br /><br />
      21) Conducting reverse engineering, decompilation or attempting to obtain the software source code;
      <br /><br />
      22) Using, copying, modifying or building mirror sites that involve our intellectual property rights;
      <br /><br />
      23) Editing, tampering or developing derivative works based on data released by us;
      <br /><br />
      24) Modifying software functions or data and spreading the altered software to the public for any purpose;
      <br /><br />
      25) Logging in via unauthorized third-party tools, plugins or hacks, or developing and distributing such tools;
      <br /><br />
      26) Posting offensive, vulgar or inappropriate content;
      <br /><br />
      27) All other content or behaviors that violate laws and regulations, infringe third-party rights or disrupt normal service operation. We will review uploaded content within 24 hours. We reserve the right to delete violating content and permanently ban accounts for serious violations.
      <br /><br />
      4.3 All content you publish represents only your personal views, not the stance of our platform. We take no responsibility for content and behaviors posted by individual users.
      <br /><br />
      4.4 You are fully accountable for the authenticity, legality and validity of all information you publish. If your actions cause losses to our company or any third party, you shall make full compensation and we reserve the right to pursue legal liabilities.
      <br /><br />
    </div>

    <h2 class="section-heading">5. Third-Party Products & Services</h2>
    <div class="content-block">
      5.1 Our services may display advertisements and promotions provided by third parties, and you agree to receive such ads during usage. Except where required by law, you shall bear all risks for transactions based on advertising information, and we will not be liable for any resulting losses.
      <br /><br />
      5.2 If you use external products or services linked in our software, you must abide by the corresponding third-party terms. We do not guarantee the safety and reliability of third-party services and assume no liability for losses caused by them.
    </div>

    <h2 class="section-heading">6. Membership Services</h2>
    <div class="content-block">
      6.1 You may purchase membership services. Specific privileges and pricing are subject to the content displayed on relevant service pages.
      <br /><br />
      6.2 We can adjust membership fees and service contents at any time by updating relevant pages.
      <br /><br />
      6.3 Membership payments are non-refundable and non-transferable under all circumstances, unless otherwise required by law. Membership will expire automatically at the end of the term, and unused service time cannot be extended or postponed.
      <br /><br />
      6.4 The personal information you submit for membership registration must be true and valid. Please update your information timely when necessary, or you will be responsible for any failure to enjoy membership rights normally.
      <br /><br />
    </div>

    <h2 class="section-heading">7. Subscriptions & Virtual Currency</h2>
    <div class="content-block">
      7.1 Subscriptions
      We provide one-month, two-month and three-month subscription plans. For mobile devices, fees will be charged to your iTunes or Google account after purchase confirmation. Subscriptions renew automatically. To turn off auto-renewal, please operate at least 24 hours before the current period ends. Renewal fees may be deducted within 24 hours prior to expiration. You can manage subscriptions in your account settings. Active subscriptions cannot be cancelled midway, while auto-renewal can be disabled at any time.
      <br /><br />
      All subscription fees are non-refundable and non-transferable, whether the service is active or discontinued. We may adjust prices, modify features, stop offering subscriptions or set validity periods at any time. Subscriptions cannot be exchanged for cash.
      <br /><br />
      7.2 Virtual Currency
      Virtual Currency such as Diamonds you purchase or receive belongs to our company, and you only have usage rights. It can be used for posting stories, applying filters, video chat and other functions. We have the right to verify your identity and qualification to use virtual currency.
      <br /><br />
      We will not provide refunds, replacements or compensation for virtual currency due to service adjustment, suspension, system errors or other reasons. Virtual currency cannot be resold, transferred or refunded even if it expires or is revoked. We may change prices and usage rules, stop issuance or set expiration dates without notice or compensation. Virtual currency has no cash value. All unused virtual currency will be forfeited without compensation if your account is deleted or terminated. Virtual currency is not regarded as personal property.
      <br /><br />
      7.3 Payment Method
      Paid services can be purchased via third-party accounts including Apple iTunes and Google Play. All charges will appear on your third-party account bill. You need to comply with the terms of service of relevant payment platforms.
      <br /><br />
      7.4 Unsubscribe
      If you subscribe through a third-party platform, you must cancel the subscription on that platform in accordance with its rules. After cancellation, you can continue using benefits until the current billing cycle ends, and no partial refund will be issued.
      <br /><br />
      7.5 Term & Fees
      This Agreement remains effective while you use our application, website and related services. We may terminate your membership at any time without notice, and no refund will be given for unused fees. This Agreement will still be valid after membership termination. We reserve the right to charge for services and terminate membership for rule violations or unpaid fees. Standard SMS, MMS and data usage fees will be charged by your mobile operator.
      <br /><br />
    </div>

    <h2 class="section-heading">8. Data Storage Rules</h2>
    <div class="content-block">
      8.1 We are not responsible for accidental deletion or failure to store your data within our services.
      <br /><br />
      8.2 We will set the maximum storage duration and capacity for user data according to actual operation. We suggest you back up important data by yourself regularly.
      <br /><br />
      8.3 If you stop using the service or the service is terminated, we may permanently delete your data from servers and have no obligation to return your data.
      <br /><br />
      8.4 You may edit or delete your personal information and published content. Please note that deleting data may lead to removal of associated content, and you shall take all relevant risks.
      <br /><br />
    </div>

    <h2 class="section-heading">9. Risk Statement</h2>
    <div class="content-block">
      9.1 Our software is only an information sharing and transmission platform. You take full responsibility for all operations under your account and all resulting consequences. You shall judge content independently and bear all risks arising from service use. We will not compensate for any losses caused by your personal behaviors.
      <br /><br />
      9.2 If you find any user violating this Agreement or using services improperly, please contact us promptly and we will take corresponding measures.
      <br /><br />
      9.3 We reserve the right to adjust, suspend, shut down or update any service functions at any time without prior notice.
      <br /><br />
    </div>

    <h2 class="section-heading">10. Intellectual Property Rights</h2>
    <div class="content-block">
      10.1 All intellectual property rights related to our services belong to us, excluding content owned by advertisers. If you already hold intellectual property rights for content you posted before using our services, those rights will remain yours.
      <br /><br />
      10.2 All copyrights, patents and other intellectual property rights of this software are owned exclusively by our company.
      <br /><br />
      10.3 The copyright and trademark of software graphics, text, logos, product and service names belong to us. You may not use or modify these logos without our written approval.
      <br /><br />
      10.4 Intellectual property owned by us and our cooperative advertisers is protected by law. No one may use or create derivative works without written permission.
      <br /><br />
      10.5 You retain intellectual property rights over the text, images, videos, audios and other content you upload. You hereby grant us and our affiliated companies an irrevocable, perpetual, exclusive, royalty-free, global and sub-licensable license to use, display, reproduce, adapt, broadcast, rent, translate and distribute such content for commercial or non-commercial purposes. We may choose usage methods freely, including editing content and authorizing third parties to use and distribute it.
    </div>

    <h2 class="section-heading">11. Liability Clauses</h2>
    <div class="content-block">
      11.1 If you engage in illegal acts, breach this Agreement or infringe others' rights, we may issue warnings, restrict partial features, block your account, add you to blacklist, publicize your violations or deduct your virtual assets. We will not refund virtual items if your account is blocked or terminated.
      <br /><br />
      11.2 We have the right to take legal action and report relevant information to official authorities against your violations, and you shall bear all final consequences.
      <br /><br />
      11.3 You shall cover all losses, claims and related expenses including attorney fees caused by your violations.
      <br /><br />
      11.4 You agree to defend and indemnify us and our affiliates against all complaints, claims, losses, costs and liabilities (including legal fees) arising from your use of services or breach of this Agreement.
      <br /><br />
      11.5 We may adopt the above remedial measures separately or in combination.
      <br /><br />
    </div>

    <h2 class="section-heading">12. Force Majeure & Disclaimer</h2>
    <div class="content-block">
      12.1 We are not liable for service interruptions caused by events beyond our control, such as natural disasters, wars, social unrest and government actions. We will make reasonable efforts to restore services as soon as possible.
      <br /><br />
      12.2 We do not warrant that the services will always be secure, available, uninterrupted or error-free. Within the scope permitted by law, we provide no explicit or implicit guarantees of any kind. We are not responsible for other users' content and behaviors.
      <br /><br />
      12.3 Internet instability, external software, computer viruses and other security threats may affect normal service use. Please protect your account, device and personal data carefully to avoid loss and harassment.
      <br /><br />
      12.4 We assume no liability for losses caused by service outages due to network faults, hacker attacks, system failures, device shutdown and other technical issues.
      <br /><br />
      12.5 The platform may contain misleading, threatening, defamatory, offensive, illegal or infringing content. We will not be responsible for any losses arising from such information.
      <br /><br />
      12.6 We will carry out regular or irregular maintenance, updates and repairs for the software. We are not liable for temporary service suspension with prior notice.
      <br /><br />
      12.7 We have the right but no obligation to handle third-party violations, and we cannot guarantee timely detection and disposal.
      <br /><br />
      12.8 We are not responsible for quality problems of free products and services, or any damages caused by them.
      <br /><br />
      12.9 We shall not be liable for indirect, incidental or consequential damages including lost profits and data loss. The maximum total compensation we pay will not exceed the total amount you have paid to us.
      <br /><br />
      12.10 Meeting or conducting offline transactions with other users carries personal and property risks. You shall take full responsibility for all offline activities.
      <br /><br />
    </div>

    <h2 class="section-heading">13. Miscellaneous Provisions</h2>
    <div class="content-block">
      13.1 Please read this Agreement carefully, especially clauses about risk tips and liability limitations. Minors must read this Agreement accompanied by their legal guardians.
      <br /><br />
      13.2 All rules and announcements published inside our software are supplementary parts of this Agreement and share the same legal effect.
      <br /><br />
      13.3 This Agreement is governed by the laws of the Hong Kong Special Administrative Region. Any disputes shall be submitted to the Hong Kong International Arbitration Centre for final resolution in accordance with its arbitration rules.
      <br /><br />
      13.4 If any clause of this Agreement is ruled invalid or unenforceable, the remaining clauses will still remain effective. Both parties will negotiate a replacement clause to achieve the original commercial purpose.
      <br /><br />
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      appName: ""
    };
  },
  created() {}
};
</script>

<style scoped>
/* 整体卡片容器 */
.agreement-container {
  max-width: 920px;
  margin: 40px auto;
  padding: 36px 45px;
  background: #fdfdfd;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  font-family: "Helvetica Neue", Arial, sans-serif;
}

/* 页面主标题 */
.page-heading {
  font-size: 24px;
  color: #1f2937;
  text-align: center;
  margin: 0 0 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 700;
}

/* 章节标题 */
.section-heading {
  font-size: 18px;
  color: #1d4ed8;
  margin: 28px 0 12px;
  padding-left: 10px;
  border-left: 5px solid #1d4ed8;
  font-weight: 600;
}

/* 正文区块 */
.content-block {
  font-size: 15px;
  line-height: 1.9;
  color: #374151;
  margin-bottom: 14px;
  text-align: left;
}

/* 移动端响应式适配 */
@media (max-width: 768px) {
  .agreement-container {
    margin: 20px;
    padding: 22px 20px;
  }
  .page-heading {
    font-size: 21px;
  }
  .section-heading {
    font-size: 17px;
  }
  .content-block {
    font-size: 14px;
  }
}
</style>